// Public presentation share-link view at /p/:uuid (no auth — gated by the
// unguessable share UUID). The client's window into a proposal.
//
// 2026-10-07 — other redesign (handoff W17 + the designer's presentation
// prototypes, ~/Dev/other-website/dist/presentation*.html):
//   - Plain white page, ink type, fine rules; the product photography is the
//     only colour. The other lockup (vector, /brand/) — never redrawn.
//   - Intro: "A few good things. Picked for you." + the rep's own note, with a
//     "Your partner at other" card carrying the rep's name / phone / email.
//   - TWO VIEWS of the SAME data:
//       Rows — Option B (CEO 2026-08-18): one row per item, the quantity ladder
//              running ACROSS so a client can compare down the column.
//       Grid — compact cards for long presentations (3–30+ products), with a
//              filter (by the rep's sections, else by category).
//     The rep's Layout choice picks the opening view (grid / cards / compact →
//     Grid); anything over 12 products opens as a Grid. The client can switch.
//   - "View details" opens a side drawer IN PLACE (handoff: details stay inside
//     the shared presentation) — full description, every price break, charges,
//     and the same note box as the row.
//   - Per-item notes live in the parent until Send; a validation or network
//     error never clears them. The sticky bar flips to "Feedback sent" with a
//     "Send more feedback" affordance (no full-page takeover).
//   - Item picks / favourites stay OFF (CEO 2026-07-27 — confusing). The POST
//     still sends item_selections: [] so the server contract is unchanged.
//   - Server-filtered: the payload carries sell prices only (wholesale, margin,
//     supplier and internal decoration notes are stripped in the route).
//   - Print: the shared DocumentPrintStyles + a print-only masthead.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { groupItemsIntoSections, sectionChromeFor } from "@merchd/shared";
import { useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Download, LayoutGrid, Rows3, X } from "lucide-react";
import { ApiError, api } from "@/lib/api";
import { friendlyApiError } from "@/lib/apiErrorCopy";
import { cn } from "@/lib/utils";
import { useAuth } from "@/auth/AuthProvider";
import { DocumentPrintStyles } from "@/components/documents/PrintStyles";
import {
  deriveBarView,
  sendMoreState,
  validateSubmit,
} from "./publicPresentationBar";

interface PublicTier {
  id: string;
  quantity: number;
  sellPrice: string;
  position: number;
}

interface PublicDecorationCharge {
  id: string;
  chargeType: string;
  label: string;
  quantity: number;
  sellPrice?: string;
  position: number;
}

interface PublicDecoration {
  id: string;
  position: number;
  location: string;
  logoName: string | null;
  charges: PublicDecorationCharge[];
}

// Item-level charges (PR #147). Same client-facing shape as decoration
// charges (sellPrice only — cost + margin stripped server-side).
interface PublicItemCharge {
  id: string;
  chargeType: string;
  label: string;
  quantity: number;
  sellPrice?: string;
  position: number;
}

interface PublicItem {
  id: string;
  productId: string;
  productSnapshot: {
    name?: string;
    description?: string;
    imageUrls?: string[] | Record<string, string>;
    sku?: string;
    categories?: string[];
  } | null;
  descriptionOverride: string | null;
  sectionId: string | null;
  position: number;
  isHidden: boolean | null;
  tiers: PublicTier[];
  decorations: PublicDecoration[];
  itemCharges?: PublicItemCharge[];
}

interface PublicPresentation {
  id: string;
  presentationNumber: number;
  status: string;
  layout: string | null;
  introduction: string | null;
  inHandsDate: string | null;
  hidePricing: boolean | null;
}

interface PublicSentBy {
  name: string | null;
  email: string | null;
  phone: string | null;
}

/** A titled group. Rendered as a heading above its products. */
interface PublicSection {
  id: string;
  title: string;
  position: number;
}

interface PublicPresentationResponse {
  presentation: PublicPresentation;
  sentBy: PublicSentBy | null;
  sections?: PublicSection[];
  items: PublicItem[];
  /**
   * True when the request was authenticated as staff and the presentation is
   * in draft. Anonymous viewers never see this; they get a 404 for drafts.
   */
  staffPreview?: boolean;
}

type ViewMode = "rows" | "grid";

/** Over this many products the page opens as a grid. */
const GRID_AT = 12;
const GRID_LAYOUTS = new Set(["grid", "cards", "compact"]);

export function PublicPresentationPage() {
  const { uuid = "" } = useParams<{ uuid: string }>();
  const [data, setData] = useState<PublicPresentationResponse | null>(null);
  const [loadError, setLoadError] = useState<"gone" | "failed" | null>(null);
  // Wait for AuthProvider's initial refresh to settle BEFORE firing the data
  // fetch — otherwise a logged-in rep gets the anonymous 404 on their own
  // drafts (child effects commit before the parent binds the token).
  const { loading: authLoading } = useAuth();
  // Per-item notes — Record<itemId, note>. Lives here so the send bar can count
  // them and Send can bundle them. Nothing posts mid-flight.
  const [itemComments, setItemComments] = useState<Record<string, string>>({});
  const [submitState, setSubmitState] = useState<
    "idle" | "saving" | "done" | "error"
  >("idle");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [view, setView] = useState<ViewMode | null>(null);
  const [filter, setFilter] = useState("");
  const [detailId, setDetailId] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    let cancelled = false;
    setLoadError(null);
    // Not skipAuth: a logged-in rep's token travels so the server can grant a
    // draft preview. skipAuthRefresh stops a refresh chase for anonymous
    // visitors.
    api<PublicPresentationResponse>(`/p/${uuid}/data`, {
      skipAuthRefresh: true,
    })
      .then((res) => {
        if (cancelled) return;
        setData(res);
        document.title = `Presentation P-${res.presentation.presentationNumber} · other`;
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err instanceof ApiError && err.status === 404 ? "gone" : "failed");
      });
    return () => {
      cancelled = true;
    };
  }, [uuid, authLoading]);

  const visibleItems = useMemo(
    () => (data?.items ?? []).filter((it) => !it.isHidden),
    [data],
  );

  // Ungrouped items lead with NO heading, then each section in order. The
  // grouping rules live in @merchd/shared so the offline gate can execute them.
  const groups = useMemo(
    () => groupItemsIntoSections(visibleItems, data?.sections ?? []),
    [data, visibleItems],
  );
  const hasSections = (data?.sections ?? []).length > 0;

  // Document order (group by group) — numbering, "Jump to", the drawer pager.
  const orderedItems = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  const numberOf = useMemo(() => {
    const m = new Map<string, number>();
    orderedItems.forEach((it, i) => m.set(it.id, i + 1));
    return m;
  }, [orderedItems]);

  // Grid filter: the rep's own sections when there are any, else categories.
  const filterOptions = useMemo(() => {
    if (hasSections) {
      return groups
        .filter((g) => g.title !== null && g.title.trim() !== "")
        .map((g) => ({ value: `s:${g.key}`, label: g.title as string }));
    }
    const cats = new Set<string>();
    for (const it of orderedItems) {
      const c = firstCategory(it);
      if (c) cats.add(c);
    }
    return cats.size > 1
      ? [...cats].sort().map((c) => ({ value: `c:${c}`, label: c }))
      : [];
  }, [groups, hasSections, orderedItems]);

  const filteredGroups = useMemo(() => {
    if (!filter) return groups;
    if (filter.startsWith("s:")) return groups.filter((g) => `s:${g.key}` === filter);
    const cat = filter.slice(2);
    return groups
      .map((g) => ({ ...g, items: g.items.filter((it) => firstCategory(it) === cat) }))
      .filter((g) => g.items.length > 0);
  }, [groups, filter]);

  const mode: ViewMode =
    view ??
    (GRID_LAYOUTS.has(data?.presentation.layout ?? "") || visibleItems.length > GRID_AT
      ? "grid"
      : "rows");
  const shownItems = mode === "grid" ? filteredGroups.flatMap((g) => g.items) : orderedItems;

  const setItemComment = useCallback(
    (itemId: string, note: string): void => {
      setItemComments((prev) => ({ ...prev, [itemId]: note }));
      // The needs-a-note / API error line clears the moment the client types.
      setSubmitError(null);
    },
    [],
  );

  // Non-empty (trimmed) item notes, ready to POST as item_comments.
  const filledItemComments = useMemo(
    () =>
      Object.entries(itemComments)
        .map(([item_id, note]) => ({ item_id, comment: note.trim() }))
        .filter((c) => c.comment.length > 0),
    [itemComments],
  );

  async function handleSubmit(): Promise<void> {
    const check = validateSubmit(filledItemComments.length);
    if (!check.ok) {
      // Nothing to send — say so in the bar, where the client is looking.
      setSubmitError(check.error);
      return;
    }
    setSubmitState("saving");
    setSubmitError(null);
    try {
      // item_selections is POSTed EMPTY: the client-facing pick affordance was
      // removed (CEO 2026-07-27). The server side stays wired, dormant.
      await api(`/p/${uuid}/feedback`, {
        method: "POST",
        body: JSON.stringify({
          item_comments: filledItemComments,
          item_selections: [],
        }),
        skipAuth: true,
      });
      setSubmitState("done");
    } catch (err) {
      // Notes are NOT cleared on failure — the client retries with them intact.
      setSubmitState("error");
      const code =
        err instanceof ApiError
          ? ((err.body as { error?: string } | null)?.error ?? null)
          : null;
      setSubmitError(
        friendlyApiError(code) ??
          "We couldn't send your feedback. Nothing was lost — try again.",
      );
    }
  }

  // "Send more feedback" — the just-sent notes were delivered (the server
  // appends repeat submissions), so clear the boxes for a fresh round.
  function handleSendMore(): void {
    const reset = sendMoreState();
    setItemComments(reset.itemComments);
    setSubmitState(reset.submitState);
    setSubmitError(reset.error);
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-canvas font-sans text-ink">
        <SiteHeader />
        <main className="mx-auto max-w-[640px] px-4 py-24 sm:px-6">
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-muted">
            Shared presentation
          </p>
          {loadError === "gone" ? (
            <>
              <h1 className="mt-4 text-[32px] font-semibold leading-[1.1] tracking-[-1px]">
                This link isn't active any more.
              </h1>
              <p className="mt-4 text-[15px] leading-relaxed text-muted">
                It may have been replaced by a newer version. Reply to the email
                that brought you here and your contact at other will send a
                fresh link.
              </p>
            </>
          ) : (
            <>
              <h1 className="mt-4 text-[32px] font-semibold leading-[1.1] tracking-[-1px]">
                We couldn't load this presentation.
              </h1>
              <p className="mt-4 text-[15px] leading-relaxed text-muted">
                Something went wrong on our side. Nothing has changed — try again
                in a moment.
              </p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="mt-6 inline-flex min-h-[44px] items-center rounded bg-ink px-5 text-sm font-medium text-white hover:bg-ink-700"
              >
                Try again
              </button>
            </>
          )}
        </main>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-canvas font-sans" aria-busy="true">
        <SiteHeader />
        <div className="mx-auto max-w-[1320px] px-4 py-14 sm:px-6 lg:px-12">
          <span className="sr-only">Loading presentation…</span>
          <div className="h-3 w-48 animate-pulse rounded bg-white" />
          <div className="mt-6 h-12 w-2/3 animate-pulse rounded bg-white" />
          <div className="mt-10 grid grid-cols-2 gap-6 md:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="aspect-square animate-pulse rounded bg-white" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const { presentation, sentBy } = data;
  const hidePricing = presentation.hidePricing ?? false;
  const presentedByLine = buildPresentedByLine(sentBy);
  const barView = deriveBarView({
    submitState,
    noteCount: filledItemComments.length,
    error: submitError,
  });
  const many = visibleItems.length > GRID_AT;
  const detailItem = detailId ? orderedItems.find((it) => it.id === detailId) ?? null : null;

  return (
    <div className="min-h-screen bg-canvas font-sans text-ink">
      <DocumentPrintStyles />

      <SiteHeader onPrint={() => window.print()} />

      {/* pb-36 clears the fixed send bar so it never covers the last item. */}
      <main className="mx-auto max-w-[1320px] px-4 pb-36 sm:px-6 lg:px-12 print:pb-0">
        {/* Staff-preview banner. Only a logged-in rep previewing a draft gets
            staffPreview: true from the server. */}
        {data.staffPreview && (
          <div className="mt-5 rounded border border-rule bg-white px-3 py-2 text-[13px] text-muted print:hidden">
            <strong className="font-semibold text-ink">Preview mode.</strong>{" "}
            You're viewing this as a rep — it hasn't been sent to the client
            yet. The client's link won't open until you press "Send to client"
            on the presentation.
          </div>
        )}

        {/* Print-only masthead. The screen header carries the rep's details in
            the partner card; the PDF a client saves and forwards needs them too. */}
        <div className="hidden print:block print:mb-6 print:border-b print:border-rule print:pb-4">
          <div className="flex items-start justify-between gap-8">
            <img
              src="/brand/other-primary-lockup-black.svg"
              alt="other — merch. paper. gifts."
              style={{ height: "34px", width: "auto", display: "block" }}
            />
            <div className="text-right text-[11px] leading-relaxed text-muted">
              <div className="font-semibold text-ink">
                Presentation P-{presentation.presentationNumber}
              </div>
              {presentedByLine && <div>{presentedByLine}</div>}
              <div>
                {new Date().toLocaleDateString(undefined, {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Hero — left-aligned at the page's content width (CEO 2026-07-31).
            The rep's partner card sits to the right on desktop, below on a
            phone. The rep's note PRINTS; the headline is screen chrome. */}
        <section className="mb-0 grid gap-8 py-10 md:grid-cols-[minmax(0,1fr)_240px] md:items-end md:gap-12 md:py-12 print:py-2">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-ink print:hidden">
              Presentation P-{presentation.presentationNumber}
              {presentation.inHandsDate
                ? ` · Needed by ${formatDay(presentation.inHandsDate)}`
                : ""}
            </p>
            <h1 className="mt-4 text-[40px] font-semibold leading-[1.02] tracking-[-0.05em] text-ink sm:text-[52px] lg:text-[64px] print:hidden">
              {many ? "Good things." : "A few good things."}
              <br />
              <span className="font-normal text-muted">Picked for you.</span>
            </h1>
            <p className="mt-5 whitespace-pre-wrap text-[15px] leading-relaxed text-muted">
              {presentation.introduction?.trim()
                ? presentation.introduction
                : "Thoughtful pieces for your people. Take a look, leave a note on anything you like, and tell us what you'd like to make yours."}
            </p>
          </div>
          {sentBy && (sentBy.name || sentBy.email || sentBy.phone) && (
            <aside className="border-l border-rule pl-6 print:hidden" aria-label="Your contact">
              <span className="block text-[11px] uppercase tracking-[0.08em] text-muted">
                Your partner at other
              </span>
              {sentBy.name && <strong className="mt-3 block text-[15px] font-semibold">{sentBy.name}</strong>}
              <p className="mt-1 text-sm leading-relaxed text-muted">
                {sentBy.phone && (
                  <a className="block hover:text-ink" href={`tel:${sentBy.phone}`}>
                    {sentBy.phone}
                  </a>
                )}
                {sentBy.email && (
                  <a className="block break-all hover:text-ink" href={`mailto:${sentBy.email}`}>
                    {sentBy.email}
                  </a>
                )}
              </p>
            </aside>
          )}
        </section>

        <HowThisWorks />

        {/* Collection bar — count, Rows/Grid switch, and the view's own
            navigation (Jump to an item / Filter). Sticky so a long grid keeps
            its controls in reach. */}
        <nav
          aria-label="Presentation view"
          className="sticky top-0 z-30 -mx-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-y border-rule bg-canvas px-4 py-3 sm:-mx-6 sm:px-6 lg:mx-0 lg:px-0 print:hidden"
        >
          <div className="flex items-center gap-5">
            <strong className="text-[15px] font-semibold">
              {visibleItems.length} {visibleItems.length === 1 ? "idea" : "ideas"}
            </strong>
            <div className="flex gap-1" role="group" aria-label="Layout">
              <ViewButton active={mode === "rows"} onClick={() => setView("rows")} label="Rows">
                <Rows3 className="h-4 w-4" aria-hidden />
              </ViewButton>
              <ViewButton active={mode === "grid"} onClick={() => setView("grid")} label="Grid">
                <LayoutGrid className="h-4 w-4" aria-hidden />
              </ViewButton>
            </div>
          </div>
          {mode === "rows" ? (
            <label className="flex min-w-0 items-center gap-2 text-xs text-muted">
              <span className="whitespace-nowrap">Jump to an item</span>
              <select
                value=""
                onChange={(e) => {
                  const el = e.target.value ? document.getElementById(`item-${e.target.value}`) : null;
                  el?.scrollIntoView({ block: "start", behavior: prefersReducedMotion() ? "auto" : "smooth" });
                }}
                className="min-h-[40px] min-w-0 max-w-[260px] rounded border border-rule bg-white px-2 text-sm text-ink"
              >
                <option value="">Choose an item</option>
                {orderedItems.map((it) => (
                  <option key={it.id} value={it.id}>
                    {pad(numberOf.get(it.id) ?? 0)} · {itemName(it)}
                  </option>
                ))}
              </select>
            </label>
          ) : filterOptions.length > 0 ? (
            <label className="flex min-w-0 items-center gap-2 text-xs text-muted">
              <span>{hasSections ? "Show" : "Category"}</span>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="min-h-[40px] min-w-0 max-w-[260px] rounded border border-rule bg-white px-2 text-sm text-ink"
              >
                <option value="">Everything</option>
                {filterOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </nav>

        {mode === "grid" && filter && (
          <p className="mt-4 text-[13px] text-muted print:hidden" aria-live="polite">
            Showing {shownItems.length} of {visibleItems.length} ideas
          </p>
        )}

        {/* Items, group by group. sectionChromeFor is the shared decision, so
            the rep's document and the client's page never disagree about what
            is grouped. */}
        {(mode === "grid" ? filteredGroups : groups).map((group, groupIndex) => {
          const chrome = sectionChromeFor(group, hasSections);
          const topGap = groupIndex === 0 ? " mt-8" : " mt-14";
          return (
            <div key={group.key}>
              {chrome.kind === "heading" && (
                <h2
                  // print:break-after-avoid keeps a heading with its products.
                  className={
                    "mb-2 border-b border-rule pb-3 text-[26px] font-semibold tracking-[-0.03em] text-ink print:break-after-avoid" +
                    topGap
                  }
                >
                  {chrome.title}
                </h2>
              )}
              {chrome.kind === "blank-heading" && (
                // A real category the rep never named (CEO 2026-08-07: "just a
                // line"). aria-hidden — an empty heading is a divider, not a
                // landmark. The nbsp holds the line box open.
                <h2
                  aria-hidden="true"
                  className={
                    "mb-2 border-b border-rule pb-3 text-[26px] font-semibold tracking-[-0.03em] text-ink print:break-after-avoid" +
                    topGap
                  }
                >
                  {" "}
                </h2>
              )}
              {chrome.kind === "caption" && (
                <p
                  className={
                    "mb-2 text-[11px] uppercase tracking-[0.08em] text-muted print:break-after-avoid" +
                    topGap
                  }
                >
                  Also included
                </p>
              )}
              {chrome.kind === "none" && groupIndex === 0 && <div className="mt-2" />}

              {mode === "rows" ? (
                <ol className="flex flex-col">
                  {group.items.map((it) => (
                    <PublicItemCard
                      key={it.id}
                      item={it}
                      number={numberOf.get(it.id) ?? 0}
                      comment={itemComments[it.id] ?? ""}
                      onCommentChange={(note) => setItemComment(it.id, note)}
                      onOpenDetails={() => setDetailId(it.id)}
                      hidePricing={hidePricing}
                    />
                  ))}
                </ol>
              ) : (
                <ol className="mt-4 grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 md:gap-x-6 xl:grid-cols-4">
                  {group.items.map((it) => (
                    <PublicGridCard
                      key={it.id}
                      item={it}
                      number={numberOf.get(it.id) ?? 0}
                      hasNote={(itemComments[it.id] ?? "").trim().length > 0}
                      onOpenDetails={() => setDetailId(it.id)}
                      hidePricing={hidePricing}
                    />
                  ))}
                </ol>
              )}
            </div>
          );
        })}

        {visibleItems.length === 0 && (
          <p className="py-16 text-center text-muted">
            Your rep is still choosing products. Check back soon.
          </p>
        )}

        <section className="pt-14 text-center print:hidden">
          <h2 className="text-[30px] font-semibold tracking-[-0.04em] sm:text-[36px]">
            Let's make it yours.
          </h2>
          <p className="mt-3 text-[15px] text-muted">
            Leave a note on anything you like. We'll work through the details together.
          </p>
        </section>

        <footer className="mt-16 flex flex-col items-center gap-4 border-t border-rule pt-8 text-[13px] text-muted print:hidden">
          <img
            src="/brand/other-primary-lockup-black.svg"
            alt="other — merch. paper. gifts."
            style={{ height: "30px", width: "auto", display: "block" }}
          />
          <div className="flex flex-wrap justify-center gap-6">
            <a href="https://withother.com/privacy" className="hover:text-ink">Privacy</a>
            <a href="https://withother.com/terms" className="hover:text-ink">Terms</a>
            <a href="mailto:hello@withother.com" className="hover:text-ink">Contact</a>
          </div>
          <p className="text-[11px]">© 2026 other</p>
        </footer>
      </main>

      {/* Sticky send bar — live note count + one Send button. Every send-bar
          state (needs-a-note, API error, sent) renders HERE, in view. */}
      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-rule bg-canvas/95 backdrop-blur-md print:hidden">
        <div className="mx-auto flex max-w-[1320px] items-center justify-between gap-4 px-4 py-3 sm:px-6 sm:py-4 lg:px-12">
          <div className="min-w-0" aria-live="polite">
            {barView.kind === "sent" ? (
              <p className="flex items-center gap-2 text-sm font-semibold text-ink">
                <span
                  className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-ink text-[12px] text-white"
                  aria-hidden="true"
                >
                  ✓
                </span>
                Feedback sent
                <span className="hidden font-normal text-muted sm:inline">
                  · {sentBy?.name ? `${sentBy.name} will` : "Your rep will"} follow up shortly.
                </span>
              </p>
            ) : barView.error ? (
              <p className="text-sm font-semibold text-risk" role="alert">
                {barView.error}
              </p>
            ) : barView.noteCount === 0 ? (
              <>
                <strong className="block text-sm font-semibold">No notes yet</strong>
                <span className="hidden text-xs text-muted sm:block">
                  Add a note on any item, then send it to your rep.
                </span>
              </>
            ) : (
              <>
                <strong className="block text-sm font-semibold">
                  {`${barView.noteCount} note${barView.noteCount === 1 ? "" : "s"} ready`}
                </strong>
                <span className="hidden text-xs text-muted sm:block">
                  They send together, in one go.
                </span>
              </>
            )}
          </div>

          {barView.kind === "sent" ? (
            <button
              type="button"
              onClick={handleSendMore}
              className="min-h-[44px] shrink-0 rounded border border-rule bg-white px-4 text-sm font-medium text-ink hover:bg-[#fafaf8]"
            >
              Send more feedback
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={barView.saving}
              className="inline-flex min-h-[44px] shrink-0 items-center gap-6 rounded bg-ink px-5 text-sm font-medium text-white transition-colors hover:bg-ink-700 disabled:opacity-60"
            >
              {barView.saving ? "Sending…" : "Send feedback"}
              <span aria-hidden="true">→</span>
            </button>
          )}
        </div>
      </div>

      {detailItem && (
        <DetailsDrawer
          item={detailItem}
          number={numberOf.get(detailItem.id) ?? 0}
          list={shownItems}
          comment={itemComments[detailItem.id] ?? ""}
          onCommentChange={(note) => setItemComment(detailItem.id, note)}
          onNavigate={setDetailId}
          onClose={() => setDetailId(null)}
          hidePricing={hidePricing}
        />
      )}
    </div>
  );
}

function SiteHeader({ onPrint }: { onPrint?: () => void }) {
  return (
    <header className="border-b border-rule print:hidden">
      <div className="mx-auto flex h-[76px] max-w-[1320px] items-center justify-between px-4 sm:px-6 lg:px-12">
        <img
          src="/brand/other-primary-lockup-black.svg"
          alt="other — merch. paper. gifts."
          style={{ height: "40px", width: "auto", display: "block" }}
        />
        {onPrint && (
          <button
            type="button"
            onClick={onPrint}
            className="inline-flex min-h-[44px] items-center gap-1.5 text-sm text-ink hover:text-muted"
          >
            <Download className="h-4 w-4" aria-hidden /> Save as PDF
          </button>
        )}
      </div>
    </header>
  );
}

function ViewButton(props: {
  active: boolean;
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      aria-pressed={props.active}
      className={cn(
        "inline-flex min-h-[40px] items-center gap-1.5 rounded px-2.5 text-sm transition-colors",
        props.active ? "bg-ink text-white" : "text-muted hover:bg-white hover:text-ink",
      )}
    >
      {props.children}
      {props.label}
    </button>
  );
}

// "How this works" — 3-step orientation strip (restored 2026-07-27 per CEO).
const HOW_STEPS: Array<{ n: number; title: string; body: string }> = [
  {
    n: 1,
    title: "Browse the products",
    body: "Everything we picked for you — photos, details and pricing are all here.",
  },
  {
    n: 2,
    title: "Add a note on any item",
    body: "Love it? Want it in navy? Need 250 instead of 100? Type it in the note box with each product.",
  },
  {
    n: 3,
    title: "Send your feedback",
    body: "Press Send feedback in the bar at the bottom and your rep will follow up.",
  },
];

function HowThisWorks() {
  return (
    <section aria-label="How this works" className="mb-6 print:hidden">
      <ol className="grid gap-3 md:grid-cols-3 md:gap-8">
        {HOW_STEPS.map((s) => (
          <li key={s.n} className="flex gap-3">
            <span className="text-xs font-semibold tabular-nums text-muted">{pad(s.n)}</span>
            <span className="text-[13px] leading-snug text-muted">
              <span className="font-semibold text-ink">{s.title}.</span> {s.body}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

interface ItemCardProps {
  item: PublicItem;
  number: number;
  comment: string;
  onCommentChange: (note: string) => void;
  onOpenDetails: () => void;
  hidePricing: boolean;
}

// ── OPTION B · ROWS ────────────────────────────────────────────────────────
// 🔴 The team chose this 2026-08-18: one row per item, the quantity ladder
// running ACROSS, so a client can compare products without scrolling past
// full-bleed cards ("dated, images too large, too much" was the complaint).
// The other redesign keeps that skeleton and gives it the designer's type:
// item number, meta line, a strong name, the ladder, then the note box.
function PublicItemCard({
  item,
  number,
  comment,
  onCommentChange,
  onOpenDetails,
  hidePricing,
}: ItemCardProps) {
  const snap = item.productSnapshot ?? {};
  const description = item.descriptionOverride ?? snap.description ?? "";
  const name = snap.name ?? "Untitled product";
  const imageUrl = primaryImage(item);
  const category = firstCategory(item);
  const visibleTiers = sortedTiers(item);

  // The photo opens the details drawer (full-size photo + every detail).
  const thumb = (
    <button
      type="button"
      onClick={onOpenDetails}
      aria-label={`View details: ${name}`}
      className="relative aspect-square w-full cursor-zoom-in md:row-span-2 overflow-hidden rounded bg-white focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#63768b]"
    >
      {imageUrl ? (
        <img src={imageUrl} alt={name} className="h-full w-full object-contain mix-blend-multiply" loading="lazy" />
      ) : (
        <span className="flex h-full items-center justify-center text-[10px] uppercase tracking-wide text-muted">
          No image
        </span>
      )}
      <span className="absolute left-2.5 top-2 text-[11px] tabular-nums text-muted">{pad(number)}</span>
    </button>
  );

  const hasNote = comment.trim().length > 0;

  // The quantity ladder, side by side — every break visible at once.
  const breaksEl = !hidePricing && visibleTiers.length > 0 && (
    <div className="flex shrink-0 overflow-hidden rounded border border-rule bg-white">
      {visibleTiers.map((t) => (
        <div
          key={t.id}
          className="min-w-[68px] flex-1 border-r border-rule px-2.5 py-2 text-center last:border-r-0"
        >
          <span className="block text-[11px] tabular-nums text-muted">
            {t.quantity.toLocaleString()}+
          </span>
          <span className="block text-sm font-semibold tabular-nums text-ink">
            {formatCurrency(t.sellPrice)}
          </span>
        </div>
      ))}
    </div>
  );

  // An unpriced item still says something honest — never a zero.
  const inquireEl = !hidePricing && visibleTiers.length === 0 && (
    <p className="rounded border border-rule bg-white px-3 py-2 text-sm">
      <strong className="block font-semibold">Inquire for pricing</strong>
      <span className="block text-xs text-muted">Your rep will confirm quantities and price.</span>
    </p>
  );

  const noteEl = (
    <label className="block print:hidden">
      <span className="text-[13px] font-semibold text-ink">Your thoughts</span>
      <textarea
        rows={hasNote ? 3 : 2}
        value={comment}
        maxLength={4000}
        onChange={(e) => onCommentChange(e.target.value)}
        placeholder="Love it? Need a different color or quantity?"
        aria-label={`Your notes on ${name}`}
        className={cn(
          "mt-2 block w-full resize-y rounded border bg-white p-3 text-sm text-ink outline-none transition-colors placeholder:text-[#8a8a85]",
          "focus:border-ink",
          hasNote ? "border-ink" : "border-rule",
        )}
      />
    </label>
  );

  return (
    <li
      id={`item-${item.id}`}
      className="scroll-mt-20 border-b border-rule py-7 last:border-b-0 print:break-inside-avoid print:py-4"
    >
      <div className="grid grid-cols-[112px_minmax(0,1fr)] items-start gap-4 md:grid-cols-[200px_minmax(0,1fr)_auto] md:gap-8">
        {thumb}
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.06em] text-muted">
            {[category, snap.sku].filter(Boolean).join(" · ") || "\u00A0"}
          </p>
          <h3 className="mt-1.5 text-[19px] font-semibold leading-tight tracking-[-0.03em] md:text-[24px]">
            {name}
          </h3>
          {description && (
            // Clamped: the full text lives in the drawer (and prints).
            <p className="mt-2 line-clamp-3 whitespace-pre-wrap text-sm leading-relaxed text-muted print:line-clamp-none">
              {description}
            </p>
          )}
          <button
            type="button"
            onClick={onOpenDetails}
            className="mt-3 border-b border-ink pb-0.5 text-sm text-ink hover:text-muted print:hidden"
          >
            {visibleTiers.length > 0 && !hidePricing ? "Details & price breaks →" : "View details →"}
          </button>
        </div>
        {/* The ladder: its own column on desktop (compare DOWN the page); a
            full-width line under the photo + copy on a phone. */}
        <div className="col-span-2 overflow-x-auto md:col-span-1 md:max-w-[420px]">
          {breaksEl}
          {inquireEl}
        </div>
        {/* Charges and decorations stay, but subordinate — a footnote to the
            item. Then the client's note, once, at the foot of the row. On
            desktop this sits under the copy, beside the photo. */}
        <div className="col-span-2 space-y-3 md:col-start-2 md:-mt-2">
          <PublicItemCharges charges={item.itemCharges ?? []} hidePricing={hidePricing} />
          <PublicDecorations decorations={item.decorations ?? []} hidePricing={hidePricing} />
          {noteEl}
        </div>
      </div>
    </li>
  );
}

// Compact card for the grid view. Everything else lives in the drawer.
function PublicGridCard(props: {
  item: PublicItem;
  number: number;
  hasNote: boolean;
  onOpenDetails: () => void;
  hidePricing: boolean;
}) {
  const { item, number, hasNote, onOpenDetails, hidePricing } = props;
  const name = itemName(item);
  const imageUrl = primaryImage(item);
  const category = firstCategory(item);
  const tiers = sortedTiers(item);
  const from = lowestPrice(tiers);
  return (
    <li className="min-w-0 print:break-inside-avoid">
      <button
        type="button"
        onClick={onOpenDetails}
        aria-label={`View details: ${name}`}
        className="relative block aspect-square w-full overflow-hidden rounded bg-white focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#63768b]"
      >
        {imageUrl ? (
          <img src={imageUrl} alt="" className="h-full w-full object-contain mix-blend-multiply" loading="lazy" />
        ) : (
          <span className="flex h-full items-center justify-center text-[10px] uppercase tracking-wide text-muted">
            No image
          </span>
        )}
        <span className="absolute left-2.5 top-2 text-[11px] tabular-nums text-muted">{pad(number)}</span>
        {hasNote && (
          <span className="absolute right-2 top-2 rounded bg-white px-1.5 py-0.5 text-[11px] font-medium text-ink shadow-sm">
            Note added
          </span>
        )}
      </button>
      {category && <p className="mt-3 text-[10px] uppercase tracking-[0.06em] text-muted">{category}</p>}
      <h3 className={cn("text-[15px] font-semibold leading-snug tracking-[-0.02em] md:text-[17px]", category ? "mt-1" : "mt-3")}>
        <button type="button" onClick={onOpenDetails} className="text-left hover:underline">
          {name}
        </button>
      </h3>
      {!hidePricing && (
        <p className="mt-1 text-sm">
          {from !== null ? (
            <>
              From <strong className="font-semibold tabular-nums">{formatCurrency(String(from))}</strong> each
            </>
          ) : (
            <span className="text-muted">Inquire for pricing</span>
          )}
        </p>
      )}
      <button
        type="button"
        onClick={onOpenDetails}
        className="mt-2 min-h-[32px] text-[13px] text-ink hover:text-muted print:hidden"
      >
        View details →
      </button>
    </li>
  );
}

// Details drawer — opens IN PLACE (handoff W10/W17). Keeps the page behind it;
// Escape or the close button returns focus to where the client was.
function DetailsDrawer(props: {
  item: PublicItem;
  number: number;
  list: PublicItem[];
  comment: string;
  onCommentChange: (note: string) => void;
  onNavigate: (id: string) => void;
  onClose: () => void;
  hidePricing: boolean;
}) {
  const { item, number, list, comment, onCommentChange, onNavigate, onClose, hidePricing } = props;
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  // Latest onClose without re-running the focus/scroll-lock effect: the parent
  // re-renders on every keystroke in the note box.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const returnFocus = useRef<HTMLElement | null>(
    typeof document !== "undefined" ? (document.activeElement as HTMLElement | null) : null,
  );
  const snap = item.productSnapshot ?? {};
  const name = itemName(item);
  const description = item.descriptionOverride ?? snap.description ?? "";
  const images = allImages(item);
  const [imageIndex, setImageIndex] = useState(0);
  const tiers = sortedTiers(item);
  const index = list.findIndex((it) => it.id === item.id);
  const prev = index > 0 ? list[index - 1] : null;
  const next = index >= 0 && index < list.length - 1 ? list[index + 1] : null;

  useEffect(() => {
    setImageIndex(0);
    panelRef.current?.scrollTo({ top: 0 });
  }, [item.id]);

  useEffect(() => {
    closeRef.current?.focus();
    const restore = returnFocus.current;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab" || !panelRef.current) return;
      const focusables = panelRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], textarea, select, input, [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      restore?.focus?.({ preventScroll: true });
    };
  }, []);

  const image = images[imageIndex] ?? null;

  return (
    <div className="fixed inset-0 z-50 print:hidden">
      <div className="absolute inset-0 bg-ink/25" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="item-details-title"
        className="absolute inset-y-0 right-0 flex w-full max-w-[520px] flex-col overflow-y-auto bg-white shadow-lg motion-safe:animate-in motion-safe:slide-in-from-right motion-safe:duration-200"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-rule bg-white px-5 py-3 sm:px-8">
          <div className="flex items-center gap-2 text-xs text-muted">
            <button
              type="button"
              onClick={() => prev && onNavigate(prev.id)}
              disabled={!prev}
              aria-label="Previous item"
              className="inline-flex h-10 w-10 items-center justify-center rounded border border-rule text-ink hover:bg-canvas disabled:opacity-35"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
            </button>
            <span className="tabular-nums">
              {index + 1} / {list.length}
            </span>
            <button
              type="button"
              onClick={() => next && onNavigate(next.id)}
              disabled={!next}
              aria-label="Next item"
              className="inline-flex h-10 w-10 items-center justify-center rounded border border-rule text-ink hover:bg-canvas disabled:opacity-35"
            >
              <ChevronRight className="h-4 w-4" aria-hidden />
            </button>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close details"
            className="inline-flex h-11 w-11 items-center justify-center rounded text-ink hover:bg-canvas"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        <div className="px-5 pb-10 pt-6 sm:px-8">
          <div className="relative aspect-square max-h-[360px] w-full overflow-hidden rounded bg-white">
            {image ? (
              <img src={image} alt={name} className="h-full w-full object-contain mix-blend-multiply" />
            ) : (
              <span className="flex h-full items-center justify-center text-xs uppercase tracking-wide text-muted">
                No image
              </span>
            )}
            <span className="absolute left-3 top-2.5 text-xs tabular-nums text-muted">{pad(number)}</span>
          </div>
          {images.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto">
              {images.slice(0, 8).map((src, i) => (
                <button
                  key={src}
                  type="button"
                  onClick={() => setImageIndex(i)}
                  aria-label={`Photo ${i + 1} of ${images.length}`}
                  aria-pressed={i === imageIndex}
                  className={cn(
                    "h-14 w-14 shrink-0 overflow-hidden rounded border bg-white",
                    i === imageIndex ? "border-ink" : "border-rule",
                  )}
                >
                  <img src={src} alt="" className="h-full w-full object-contain mix-blend-multiply" />
                </button>
              ))}
            </div>
          )}

          <p className="mt-6 text-[11px] uppercase tracking-[0.06em] text-muted">
            {[firstCategory(item), snap.sku].filter(Boolean).join(" · ")}
          </p>
          <h2 id="item-details-title" className="mt-1.5 text-[28px] font-semibold leading-tight tracking-[-0.04em]">
            {name}
          </h2>
          {description && (
            <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-muted">{description}</p>
          )}

          {!hidePricing && (
            tiers.length > 0 ? (
              <table className="mt-6 w-full text-sm">
                <caption className="sr-only">Price per piece by quantity</caption>
                <thead>
                  <tr className="border-b border-rule text-left text-xs text-muted">
                    <th scope="col" className="py-2.5 font-medium">Quantity</th>
                    <th scope="col" className="py-2.5 text-right font-medium">Price each</th>
                  </tr>
                </thead>
                <tbody>
                  {tiers.map((t) => (
                    <tr key={t.id} className="border-b border-rule">
                      <td className="py-3 tabular-nums">{t.quantity.toLocaleString()}+</td>
                      <td className="py-3 text-right font-semibold tabular-nums">{formatCurrency(t.sellPrice)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="mt-6 border-y border-rule py-3 text-sm">
                <strong className="font-semibold">Inquire for pricing.</strong>{" "}
                <span className="text-muted">Your rep will confirm quantities and price.</span>
              </p>
            )
          )}

          <div className="mt-5 space-y-3">
            <PublicItemCharges charges={item.itemCharges ?? []} hidePricing={hidePricing} />
            <PublicDecorations decorations={item.decorations ?? []} hidePricing={hidePricing} />
          </div>

          <label className="mt-6 block">
            <span className="text-[13px] font-semibold">Your thoughts</span>
            <textarea
              rows={4}
              value={comment}
              maxLength={4000}
              onChange={(e) => onCommentChange(e.target.value)}
              placeholder="A different color, quantity, or something else?"
              aria-label={`Your notes on ${name} (details)`}
              className="mt-2 block w-full resize-y rounded border border-rule bg-white p-3 text-sm text-ink outline-none placeholder:text-[#8a8a85] focus:border-ink"
            />
          </label>
          <p className="mt-2 text-xs text-muted">
            Your notes stay with this item. Press Send feedback at the bottom of the page when you're ready.
          </p>
        </div>
      </div>
    </div>
  );
}

// Decoration block — client-facing view of per-item decoration: location +
// visible charges (label + sell price). The server sends ONLY these fields
// (allowlist in the /p/:uuid/data route); the rep controls each charge's
// visibility with `display_to_client`.

const PUBLIC_CHARGE_LABELS: Record<string, string> = {
  setup: "Setup",
  run: "Per piece",
  digitizing: "Digitizing",
  rush: "Rush",
  screen: "Screen",
  pms_match: "PMS match",
  other: "Add-on",
};

function PublicDecorations(props: {
  decorations: PublicDecoration[];
  hidePricing: boolean;
}) {
  const { decorations, hidePricing } = props;
  const visible = [...decorations]
    .filter((d) => d.charges.length > 0)
    .sort((a, b) => a.position - b.position);
  if (visible.length === 0) return null;

  return (
    <div className="border-t border-rule pt-3">
      <p className="text-[11px] uppercase tracking-[0.06em] text-muted">Decoration</p>
      <ul className="mt-1.5 flex flex-col gap-2">
        {visible.map((d) => {
          const sorted = [...d.charges].sort((a, b) => a.position - b.position);
          return (
            <li key={d.id}>
              <p className="text-sm font-semibold text-ink">
                {d.location}
                {d.logoName && <span className="ml-1.5 font-normal text-muted">· {d.logoName}</span>}
              </p>
              <ul className="mt-1 flex flex-col gap-0.5">
                {sorted.map((c) => (
                  <li key={c.id} className="flex flex-wrap items-baseline gap-x-2 text-[13px] text-muted">
                    <span>
                      <span className="mr-1.5 text-ink">{PUBLIC_CHARGE_LABELS[c.chargeType] ?? c.chargeType}</span>
                      {c.label}
                      {c.quantity > 1 && <span className="ml-1.5">× {c.quantity.toLocaleString()}</span>}
                    </span>
                    {!hidePricing && c.sellPrice !== undefined && (
                      <span className="font-semibold tabular-nums text-ink">{formatCurrency(c.sellPrice)}</span>
                    )}
                  </li>
                ))}
              </ul>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// Item-level charges (PR #147). 🔴 ONE LINE, NO PANEL (CEO 2026-08-18: "too
// much space taken up by the setup charges") — a footnote to the item.
function PublicItemCharges(props: {
  charges: PublicItemCharge[];
  hidePricing: boolean;
}) {
  const { charges, hidePricing } = props;
  if (charges.length === 0) return null;
  const sorted = [...charges].sort((a, b) => a.position - b.position);
  return (
    <ul className="flex flex-wrap items-baseline gap-x-5 gap-y-1">
      {sorted.map((c) => (
        <li key={c.id} className="text-[13px] text-muted">
          <span className="mr-1.5 text-ink">{PUBLIC_CHARGE_LABELS[c.chargeType] ?? c.chargeType}</span>
          {c.label}
          {c.quantity > 1 && <span className="ml-1.5">× {c.quantity.toLocaleString()}</span>}
          {!hidePricing && c.sellPrice !== undefined && (
            <span className="ml-1.5 font-semibold tabular-nums text-ink">{formatCurrency(c.sellPrice)}</span>
          )}
        </li>
      ))}
    </ul>
  );
}

function itemName(item: PublicItem): string {
  return item.productSnapshot?.name ?? "Untitled product";
}

function allImages(item: PublicItem): string[] {
  const urls = item.productSnapshot?.imageUrls;
  const list = Array.isArray(urls)
    ? urls
    : urls && typeof urls === "object"
      ? Object.values(urls)
      : [];
  return list.filter((u): u is string => typeof u === "string" && u.length > 0);
}

function primaryImage(item: PublicItem): string | undefined {
  return allImages(item)[0];
}

function firstCategory(item: PublicItem): string | undefined {
  const cats = item.productSnapshot?.categories;
  return Array.isArray(cats)
    ? cats.find((c) => typeof c === "string" && c.trim().length > 0)
    : undefined;
}

function sortedTiers(item: PublicItem): PublicTier[] {
  return [...item.tiers].sort((a, b) => a.position - b.position || a.quantity - b.quantity);
}

function lowestPrice(tiers: PublicTier[]): number | null {
  const prices = tiers
    .map((t) => Number(t.sellPrice))
    .filter((n) => Number.isFinite(n) && n > 0);
  return prices.length > 0 ? Math.min(...prices) : null;
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

function formatDay(iso: string): string {
  return new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function buildPresentedByLine(sentBy: PublicSentBy | null): string | null {
  if (!sentBy) return null;
  const parts: string[] = [];
  if (sentBy.name && sentBy.name.trim().length > 0) parts.push(sentBy.name);
  if (sentBy.phone && sentBy.phone.trim().length > 0) parts.push(sentBy.phone);
  if (sentBy.email && sentBy.email.trim().length > 0) parts.push(sentBy.email);
  if (parts.length === 0) return null;
  return `Presented by ${parts.join(" · ")}`;
}

function formatCurrency(s: string): string {
  const n = Number(s);
  if (!Number.isFinite(n)) return s;
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}
